import * as d3 from 'd3';

import {FragmentResult} from 'biojs/FragmentResult';
import {ASSEMBLY_CONSTRUCT_AXIS_ADDITIONAL_HEIGHT} from 'constants/MapsConstants';
import {PolymerAxisViewOptions} from 'molbio/maps/linear/PolymerAxisView';
import SequenceAxisView from 'molbio/maps/linear/SequenceAxisView';
import * as sequtil from 'molbio/SeqUtil';
import {getLinearHomologyStripeData} from 'molbio/visualization/FragmentVisualizationUtil';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {getTranslation} from 'util/getTransform';

export default class HomologyRegionAxisView extends SequenceAxisView {
  static override initClass(): void {
    this.prototype.className = 'homologyRegionAxis';
    this.prototype.setting = vo.HOMOLOGY_REGION_AXIS.name;
  }

  override initialize(options: PolymerAxisViewOptions): void {
    super.initialize(options);
    this.layout = this.model.get('layout').AssemblyConstructAxis;
  }

  override _drawAssemblyFragments(): void {
    const root = this.getAxisRoot();
    const sequence = this.model.get('sequence');
    const _sequence = sequence._sequence();
    root.append('g').attr('id', 'nonHomology');
    root.append('g').attr('id', 'homology');
    const homologyFragmentRegions = _sequence.fragmentResults().map((fragmentResult: FragmentResult) => ({
      start: fragmentResult.start(),
      end: fragmentResult.end(),
      color: fragmentResult.color(),
      homologyRegionOtherColor: fragmentResult.homologyRegionOtherColor(),
    }));
    for (const {start, end, color, homologyRegionOtherColor} of homologyFragmentRegions) {
      // Length in base pairs.
      const sliceLength = sequtil.length(this.slice.start(), this.slice.end(), _sequence.length());
      const sliceOverlaps = sequtil.getRangeOverlap(start, end, this.slice.start(), this.slice.end());
      if (sliceOverlaps.length > 0) {
        for (const [sliceOverlapStart, sliceOverlapEnd] of sliceOverlaps) {
          // Length in base pairs.
          const sliceOverlapLength = sequtil.length(sliceOverlapStart, sliceOverlapEnd, _sequence.length());
          const overlapWidthRatio = sliceOverlapLength / sliceLength;
          const width = this.slice.get('width')! * overlapWidthRatio;

          const basePairToTranslateX = (basePair: number): number => {
            // Distance from slice start in base pairs.
            let translateIndex: number;
            if (this.slice.start() === basePair) {
              translateIndex = 0;
            } else {
              translateIndex = sequtil.length(this.slice.start(), basePair, _sequence.length());
            }
            // How far from the beginning of the slice basePair is, relative to the size of the whole slice
            const ratio = translateIndex / sliceLength;
            return this.slice.get('width')! * ratio - this.getTypeOffset();
          };

          // Move the ticks down to make space for the larger axis. This does need to be a function function
          // instead of an arrow function so that `this` being selected is the tick instead of the view.
          root.selectAll('.tick').attr('transform', function () {
            const translation = getTranslation(d3.select(this).attr('transform'));
            return `translate(${translation[0]},${ASSEMBLY_CONSTRUCT_AXIS_ADDITIONAL_HEIGHT})`;
          });
          // Draw the tick lines.
          root.selectAll('.tick line').style('stroke-width', 1).style('stroke', 'black');

          // Non homology region, single color.
          if (homologyRegionOtherColor == null) {
            root
              .select('#nonHomology')
              .append('rect')
              .attr('x', 0)
              .attr('y', 0)
              .attr('z', 0)
              .attr('width', width)
              .attr('height', 12)
              .style('fill', color)
              .attr('transform', `translate(${basePairToTranslateX(sliceOverlapStart)}, 0)`)
              .classed('linear-axis-piece', true);
          } else {
            const homologyStripeData = getLinearHomologyStripeData(
              {
                start: sliceOverlapStart,
                end: sliceOverlapEnd,
                color,
                homologyRegionOtherColor,
              },
              basePairToTranslateX
            );
            for (const {startPoint, endPoint, color} of homologyStripeData) {
              root
                .select('#homology')
                .append('rect')
                .attr('x', 0)
                .attr('y', 0)
                .attr('z', 1)
                .attr('width', endPoint - startPoint)
                .attr('height', 12)
                .style('fill', color)
                .attr('transform', `translate(${startPoint}, 0)`)
                .classed('linear-axis-piece', true)
                .on('dblclick', (event: Event) => {
                  sequence.get('selection').set({start, end});
                  event.stopPropagation();
                });
            }
          }
        }
      }
    }
  }
}

HomologyRegionAxisView.initClass();
